import { db } from "./config";
import {
  collection, doc, getDoc, getDocs, addDoc,
  updateDoc, deleteDoc, query, where, orderBy,
  onSnapshot, serverTimestamp, arrayUnion, writeBatch
} from "firebase/firestore";

// ==========================================
// 📋 LEADS SERVICE
// ==========================================
export const LeadsService = {
  addLead: async (data) => {
    const ref = await addDoc(collection(db, "leads"), {
      ...data,
      status: "new",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: ref.id, ...data };
  },

  getAllLeads: async () => {
    const snap = await getDocs(query(collection(db, "leads"), orderBy("createdAt", "desc")));
    return snap.docs.map((d) => ({
      id: d.id, ...d.data(),
      createdAt: d.data().createdAt?.toDate?.().toISOString()
    }));
  },

  updateLead: async (id, data) => {
    await updateDoc(doc(db, "leads", id), { ...data, updatedAt: serverTimestamp() });
  },

  deleteLead: async (id) => {
    await deleteDoc(doc(db, "leads", id));
  },

  subscribeToLeads: (cb) => {
    const q = query(collection(db, "leads"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }
};

// ==========================================
// 📞 CALL LOGS SERVICE
// ==========================================
export const CallLogsService = {
  addCallLog: async (data) => {
    const ref = await addDoc(collection(db, "callLogs"), { ...data, calledAt: serverTimestamp() });
    const leadRef = doc(db, "leads", data.leadId);
    await updateDoc(leadRef, {
      status: data.disposition === "answered" ? "contacted" :
              data.disposition === "callback" ? "callback" : "contacted",
      lastCallAt: serverTimestamp(),
      nextFollowUp: data.nextFollowUp || null
    });
    return { id: ref.id, ...data };
  },

  getAllCallLogs: async () => {
    const snap = await getDocs(query(collection(db, "callLogs"), orderBy("calledAt", "desc")));
    return snap.docs.map((d) => ({
      id: d.id, ...d.data(),
      calledAt: d.data().calledAt?.toDate?.().toISOString()
    }));
  },

  subscribeToCallLogs: (cb) => {
    const q = query(collection(db, "callLogs"), orderBy("calledAt", "desc"));
    return onSnapshot(q, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }
};

// ==========================================
// ✅ TASKS SERVICE - COMPLETE
// ==========================================
export const TasksService = {

  // 🔥 CREATE SINGLE TASK
  createTask: async (data) => {
    const ref = await addDoc(collection(db, "tasks"), {
      ...data,
      status: "pending",
      progress: 0,
      assignedItems: data.assignedItems || [],
      completedItems: [],
      notes: data.notes || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: ref.id, ...data };
  },

  // 🔥 BULK CREATE TASK (50 leads ek saath)
  createBulkTask: async (data) => {
    const ref = await addDoc(collection(db, "tasks"), {
      ...data,
      status: "pending",
      progress: 0,
      completedItems: [],
      notes: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { id: ref.id, ...data };
  },

  // 🔥 GET ALL TASKS
  getAllTasks: async () => {
    const snap = await getDocs(query(collection(db, "tasks"), orderBy("createdAt", "desc")));
    return snap.docs.map((d) => ({
      id: d.id, ...d.data(),
      createdAt: d.data().createdAt?.toDate?.().toISOString()
    }));
  },

  // 🔥 GET TASKS BY MEMBER
  getTasksByMember: async (memberId) => {
    const snap = await getDocs(
      query(collection(db, "tasks"), where("assignedTo", "==", memberId), orderBy("createdAt", "desc"))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // 🔥 UPDATE ITEM (Employee data fill karega)
  updateTaskItem: async (taskId, itemId, updates) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (!taskDoc.exists()) throw new Error("Task not found!");

    const task = taskDoc.data();
    const items = (task.assignedItems || []).map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
      return item;
    });

    // Auto progress calculate
    const total = items.length;
    const completed = items.filter((i) => i.status === "completed").length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

    let taskStatus = "pending";
    if (completed === total && total > 0) taskStatus = "completed";
    else if (completed > 0) taskStatus = "in_progress";

    await updateDoc(doc(db, "tasks", taskId), {
      assignedItems: items,
      completedItems: items.filter((i) => i.status === "completed"),
      progress,
      status: taskStatus,
      updatedAt: serverTimestamp()
    });

    return { items, progress, status: taskStatus };
  },

  // 🔥 ADD NOTE
  addNoteToTask: async (taskId, note, userId, userName) => {
    const taskDoc = await getDoc(doc(db, "tasks", taskId));
    if (!taskDoc.exists()) throw new Error("Task not found!");
    const task = taskDoc.data();
    const notes = [...(task.notes || []), {
      id: Date.now().toString(),
      note,
      userId,
      userName,
      timestamp: new Date().toISOString()
    }];
    await updateDoc(doc(db, "tasks", taskId), { notes, updatedAt: serverTimestamp() });
  },

  // 🔥 UPDATE TASK
  updateTask: async (taskId, data) => {
    await updateDoc(doc(db, "tasks", taskId), { ...data, updatedAt: serverTimestamp() });
  },

  // 🔥 DELETE TASK
  deleteTask: async (taskId) => {
    await deleteDoc(doc(db, "tasks", taskId));
  },

  // 🔥 REAL-TIME
  subscribeToTasks: (cb) => {
    const q = query(collection(db, "tasks"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }
};

// ==========================================
// 👥 TEAM SERVICE
// ==========================================
export const TeamService = {
  getAllTeamMembers: async () => {
    const snap = await getDocs(query(collection(db, "users"), orderBy("joinedAt", "desc")));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },
  updateRole: async (userId, role) => {
    await updateDoc(doc(db, "users", userId), { role });
  },
  toggleStatus: async (userId, isActive) => {
    await updateDoc(doc(db, "users", userId), { isActive });
  }
};

// ==========================================
// 💰 ACCOUNTING SERVICE
// ==========================================
export const AccountingService = {

  // ========== PROJECTS ==========
  createProject: async (data) => {
    const ref = await addDoc(collection(db, "projects"), {
      ...data,
      status: "active",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: ref.id, ...data };
  },

  getAllProjects: async () => {
    const snap = await getDocs(
      query(collection(db, "projects"), orderBy("createdAt", "desc"))
    );
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.().toISOString(),
    }));
  },

  updateProject: async (id, data) => {
    await updateDoc(doc(db, "projects", id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  deleteProject: async (id) => {
    await deleteDoc(doc(db, "projects", id));
  },

  // ========== INVOICES ==========
  createInvoice: async (data) => {
    const snap = await getDocs(
      query(collection(db, "invoices"), orderBy("invoiceNo", "desc"))
    );
    let num = 1;
    if (!snap.empty) {
      const last = snap.docs[0].data().invoiceNo;
      num = parseInt(last.split("-").pop()) + 1;
    }
    const invoiceNo = `INV-${new Date().getFullYear()}-${String(num).padStart(3, "0")}`;

    const ref = await addDoc(collection(db, "invoices"), {
      ...data,
      invoiceNo,
      status: "pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: ref.id, invoiceNo, ...data };
  },

  getAllInvoices: async () => {
    const snap = await getDocs(
      query(collection(db, "invoices"), orderBy("createdAt", "desc"))
    );
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.().toISOString(),
    }));
  },

  updateInvoice: async (id, data) => {
    await updateDoc(doc(db, "invoices", id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  markInvoicePaid: async (id, paymentData) => {
    await updateDoc(doc(db, "invoices", id), {
      status: "paid",
      paidAt: serverTimestamp(),
      paymentDetails: paymentData,
      updatedAt: serverTimestamp(),
    });
  },

  deleteInvoice: async (id) => {
    await deleteDoc(doc(db, "invoices", id));
  },

  // ========== EXPENSES ==========
  addExpense: async (data) => {
    const ref = await addDoc(collection(db, "expenses"), {
      ...data,
      date: data.date || new Date().toISOString().split("T")[0],
      createdAt: serverTimestamp(),
    });
    return { id: ref.id, ...data };
  },

  getAllExpenses: async () => {
    const snap = await getDocs(
      query(collection(db, "expenses"), orderBy("date", "desc"))
    );
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate?.().toISOString(),
    }));
  },

  deleteExpense: async (id) => {
    await deleteDoc(doc(db, "expenses", id));
  },
};

// ==========================================
// 📊 DASHBOARD STATS
// ==========================================
export const DashboardService = {
  getStats: async () => {
    const [leads, calls, tasks, invoices] = await Promise.all([
      getDocs(collection(db, "leads")),
      getDocs(collection(db, "callLogs")),
      getDocs(collection(db, "tasks")),
      getDocs(collection(db, "invoices"))
    ]);
    const l = leads.docs.map((d) => d.data());
    const c = calls.docs.map((d) => d.data());
    const t = tasks.docs.map((d) => d.data());
    const i = invoices.docs.map((d) => d.data());
    return {
      totalLeads: l.length,
      newLeads: l.filter((x) => x.status === "new").length,
      interestedLeads: l.filter((x) => x.status === "interested").length,
      convertedLeads: l.filter((x) => x.status === "converted").length,
      totalCalls: c.length,
      answeredCalls: c.filter((x) => x.disposition === "answered").length,
      callbackCalls: c.filter((x) => x.disposition === "callback").length,
      totalTasks: t.length,
      pendingTasks: t.filter((x) => x.status === "pending").length,
      inProgressTasks: t.filter((x) => x.status === "in_progress").length,
      completedTasks: t.filter((x) => x.status === "completed").length,
      overdueTasks: t.filter((x) => x.status !== "completed" && x.dueDate < new Date().toISOString().split("T")[0]).length,
      totalInvoices: i.length,
      paidInvoices: i.filter((x) => x.status === "paid").length,
      pendingInvoices: i.filter((x) => x.status === "pending").length,
      totalRevenue: i.filter((x) => x.status === "paid").reduce((s, x) => s + parseFloat(x.total || 0), 0),
      pendingAmount: i.filter((x) => x.status === "pending" || x.status === "overdue").reduce((s, x) => s + parseFloat(x.total || 0), 0)
    };
  },
  subscribeToStats: (cb) => {
    const u1 = onSnapshot(collection(db, "leads"), () => DashboardService.getStats().then(cb));
    const u2 = onSnapshot(collection(db, "callLogs"), () => DashboardService.getStats().then(cb));
    const u3 = onSnapshot(collection(db, "tasks"), () => DashboardService.getStats().then(cb));
    return () => { u1(); u2(); u3(); };
  }
};